import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Monitor, GitBranch, RefreshCw, ExternalLink, Loader2, Tablet, Smartphone, Wrench, X } from "lucide-react";

const DEVICES = [
  { id: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
  { id: "tablet", label: "Tablet", icon: Tablet, width: "768px" },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: "390px" },
];

function fixPrompt(err) {
  let p = `Fix this runtime error in the preview: ${err.message}`;
  if (err.source) p += ` (${err.source}:${err.line ?? "?"})`;
  const stack = (err.stack || "").split("\n").slice(0, 3).join("\n").trim();
  if (stack) p += `\n${stack}`;
  return p;
}

export function PreviewPane({ projectId, branch, host, onSuggest }) {
  const [state, setState] = useState({ loading: true, url: null, error: null });
  const [nonce, setNonce] = useState(0);
  const [mode, setMode] = useState("static"); // static | dynamic
  const [device, setDevice] = useState("desktop");
  const [errors, setErrors] = useState([]); // ring buffer of the last 5 runtime errors from the iframe
  const tokenRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  async function stopServer() {
    if (tokenRef.current) {
      try { await api.post(`/projects/${projectId}/preview/stop`, { token: tokenRef.current }); } catch (e) {}
      tokenRef.current = null;
    }
  }

  async function start(m) {
    const useMode = m || mode;
    setState({ loading: true, url: null, error: null });
    setErrors([]);
    try {
      const { data } = await api.post(`/projects/${projectId}/preview/start`, { branch, mode: useMode });
      tokenRef.current = data.preview_id;
      setState({ loading: true, url: `${BACKEND}${data.url}`, error: null });
    } catch (e) {
      setState({ loading: false, url: null, error: formatApiError(e.response?.data?.detail) || "Could not start preview." });
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { start(); return () => { stopServer(); }; }, [projectId, branch]);

  // Runtime errors posted by the script the backend injects into proxied HTML.
  // The proxy makes origins same-ish, so we validate the payload SHAPE strictly instead.
  useEffect(() => {
    function onMessage(e) {
      const d = e.data;
      if (!d || typeof d !== "object" || d.__cadence !== "preview-error" || typeof d.message !== "string") return;
      setErrors((prev) => [...prev, {
        kind: typeof d.kind === "string" ? d.kind : "error",
        message: d.message.slice(0, 2000),
        source: typeof d.source === "string" ? d.source : undefined,
        line: typeof d.line === "number" ? d.line : undefined,
        stack: typeof d.stack === "string" ? d.stack : undefined,
      }].slice(-5));
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  function switchMode(m) {
    if (m === mode) return;
    stopServer();
    setMode(m);
    start(m);
  }

  const latestError = errors[errors.length - 1];
  const deviceWidth = (DEVICES.find((d) => d.id === device) || DEVICES[0]).width;

  return (
    <div className="flex-1 flex flex-col bg-gray-50" data-testid="preview-pane">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 text-xs text-gray-500 min-w-0">
          <Monitor className="w-3.5 h-3.5 shrink-0" />
          <span className="font-600 text-gray-700">Preview</span>
          <span className="text-gray-400">·</span>
          <span className="inline-flex items-center gap-1 font-mono truncate"><GitBranch className="w-3 h-3" /> {branch}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {DEVICES.map((d) => (
              <button key={d.id} data-testid={`preview-device-${d.id}`} onClick={() => setDevice(d.id)} title={d.label}
                className={`px-2 py-0.5 rounded-md ${device === d.id ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
                <d.icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button data-testid="preview-mode-static" onClick={() => switchMode("static")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-600 ${mode === "static" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>Files</button>
            <button data-testid="preview-mode-dynamic" onClick={() => switchMode("dynamic")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-600 ${mode === "dynamic" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>Live server</button>
          </div>
          <button data-testid="preview-refresh" onClick={() => { setErrors([]); setNonce((n) => n + 1); }} disabled={!state.url}
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1.5 disabled:opacity-40"><RefreshCw className="w-4 h-4" /></button>
          {state.url && (
            <a data-testid="preview-open-tab" href={state.url} target="_blank" rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1.5"><ExternalLink className="w-4 h-4" /></a>
          )}
        </div>
      </div>
      <div className="flex-1 relative">
        {state.loading && !state.error && (
          <div className="absolute inset-0 grid place-items-center text-gray-400 pointer-events-none">
            <div className="text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              {mode === "dynamic" && <div className="text-xs mt-2">Starting dev server…</div>}
            </div>
          </div>
        )}
        {state.error && (
          <div data-testid="preview-error" className="absolute inset-0 grid place-items-center p-8 overflow-auto">
            <div className="max-w-lg text-center">
              <div className="w-12 h-12 rounded-xl bg-gray-100 grid place-items-center mx-auto mb-3"><Monitor className="w-6 h-6 text-gray-400" /></div>
              <div className="font-600 text-gray-800">{mode === "dynamic" ? "Dev server didn't start" : "Nothing to preview yet"}</div>
              <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap text-left bg-gray-100 rounded-lg p-3 max-h-60 overflow-auto">{state.error}</pre>
              <button onClick={() => start()} className="mt-4 text-sm bg-black text-white rounded-lg px-3.5 py-2 hover:bg-gray-900 inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Retry</button>
            </div>
          </div>
        )}
        {state.url && !state.error && (
          <div className={`w-full h-full flex justify-center ${device === "desktop" ? "" : "bg-gray-100"}`}>
            <iframe key={nonce} data-testid="preview-frame" title="preview" src={state.url}
              onLoad={() => setState((s) => ({ ...s, loading: false }))}
              style={{ width: deviceWidth }}
              className="h-full border-0 bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals" />
          </div>
        )}
        {latestError && !state.error && (
          <div data-testid="preview-error-strip"
            className="absolute bottom-0 inset-x-0 bg-red-600 text-white text-xs px-3 py-1.5 flex items-center gap-2">
            <span data-testid="preview-error-count"
              className="bg-red-800 rounded-full px-1.5 py-0.5 font-600 tabular-nums shrink-0">{errors.length}</span>
            <span className="truncate flex-1 font-mono" title={latestError.message}>{latestError.message}</span>
            {onSuggest && (
              <button data-testid="preview-fix-with-agent" onClick={() => onSuggest(fixPrompt(latestError))}
                className="shrink-0 bg-white text-red-700 rounded-md px-2 py-0.5 font-600 hover:bg-red-50 inline-flex items-center gap-1">
                <Wrench className="w-3 h-3" /> Fix with agent
              </button>
            )}
            <button data-testid="preview-error-dismiss" onClick={() => setErrors([])}
              className="shrink-0 text-red-100 hover:text-white rounded-md p-0.5"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
